import { useState } from "react";

const faqs = [
  {
    q: "What is StarBookPrime and how does it work?",
    a: "StarBookPrime is a verified factory marketplace that connects global buyers directly with USA-based and international manufacturers. Buyers can browse products, place wholesale or bulk orders, and resellers can earn commission by promoting factory products on social media platforms like TikTok, YouTube, and Instagram.",
  },
  {
    q: "How do I know a factory is verified?",
    a: "Every factory on StarBookPrime goes through a rigorous verification process — we check business registration documents, certifications (ISO, FDA, CE, etc.), and factory credentials before awarding the Verified Factory badge. You can view all certifications on each factory's profile page.",
  },
  {
    q: "What is the minimum order for wholesale or bulk orders?",
    a: "The minimum order value for wholesale and container orders is $8,000 USD per factory per order. You can also request product samples first — sample costs are typically deducted from your first bulk order. Container types available include 20ft, 40ft, and 40ft HC.",
  },
  {
    q: "How do resellers and influencers earn commission?",
    a: "Resellers and influencers join for free and get unique referral links for any product they want to promote. When a customer purchases through their link, the factory's set commission rate (typically 5–15%) is credited to their account and paid out via PayPal. There is no monthly fee — ever.",
  },
  {
    q: "Is my contact information safe when I submit a buyer requirement?",
    a: "Yes, 100%. When you submit a buyer requirement, your email and WhatsApp are completely hidden from the public. Only paid verified factory members ($8/month) can access your contact details to send you quotations. You choose who to respond to — no pressure.",
  },
  {
    q: "Can international buyers purchase from StarBookPrime factories?",
    a: "Absolutely. StarBookPrime serves buyers from 120+ countries. Many factories offer both USA local warehouse shipping (2–5 business days) and overseas warehouse shipping (7–21 business days). International buyers can also apply for the Overseas Buyer Quota Program for exclusive bulk allocations.",
  },
  {
    q: "How much does it cost for a factory to join?",
    a: "Factories get a free 3-month trial with no credit card required. After the trial, it's just $8/month — less than a cup of coffee. This gives you unlimited product listings, access to buyer leads, analytics, and the Verified Factory badge on all your listings.",
  },
  {
    q: "What platforms can resellers use to promote products?",
    a: "Resellers and stars can promote verified factory products on any platform: TikTok, YouTube, Facebook, Instagram, Twitter/X, Douyin, WeChat, and more. Your unique referral link works across all platforms and tracks every click and sale automatically.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-16 px-6 md:px-12 bg-white">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4">FAQ</span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h2>
          <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
            Everything you need to know about buying, selling, and earning on StarBookPrime — the verified factory marketplace.
          </p>
        </div>

        {/* FAQ List */}
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`rounded-xl border transition-all duration-200 overflow-hidden ${openIndex === i ? "border-orange-200 bg-orange-50/30" : "border-gray-100 bg-white hover:border-orange-100"}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
              >
                <h4 className="text-sm font-semibold text-gray-900 pr-4 leading-snug">{faq.q}</h4>
                <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${openIndex === i ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-400"}`}>
                  <i className={`${openIndex === i ? "ri-subtract-line" : "ri-add-line"} text-sm`} />
                </div>
              </button>
              {openIndex === i && (
                <div className="px-6 pb-5">
                  <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center bg-gray-50 rounded-2xl p-8 border border-gray-100">
          <p className="text-sm font-semibold text-gray-700 mb-1">Still have questions?</p>
          <p className="text-xs text-gray-500 mb-4">Our team is here to help buyers, factories, and resellers get started.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:support@starbookprime.com"
              className="flex items-center justify-center gap-2 px-6 py-2.5 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line" /> Email Support
            </a>
            <a
              href="https://wa.me/1234567890"
              rel="nofollow"
              target="_blank"
              className="flex items-center justify-center gap-2 px-6 py-2.5 border border-gray-200 text-gray-700 text-sm font-semibold rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-green-500" /> WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export { faqs };
